import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  GouvDvfDataDto,
  PropertyApplicationDto,
  PropertyApplicationService,
  PropertyDto,
} from '@workspace/data-types';
import { LuxonDatePipe } from '@workspace/ng-aura';
import { PropertyDvfGaugeComponent } from '../../../property/components/property-dvf-gauge/property-dvf-gauge.component';
import { EtxeEnumsNiceLabelUtils } from '../../../utils/etxe-enums-nice-label.utils';

@Component({
  selector: 'etxe-property-application-engagement-display',
  imports: [DecimalPipe, MatIconModule, MatTooltipModule, LuxonDatePipe, PropertyDvfGaugeComponent],
  templateUrl: './property-application-engagement-display.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyApplicationEngagementDisplayComponent {
  readonly application = input.required<PropertyApplicationDto>();
  readonly property = input<PropertyDto | null>(null);

  readonly initialCommissionRate = computed<number | null>(() => {
    return this.application().engagement?.initialCommissionRate ?? null;
  });

  readonly finalCommissionRate = computed<number | null>(() => {
    return this.application().engagement?.finalCommissionRate ?? null;
  });

  readonly hasFinalOffer = computed<boolean>(() => {
    return this.finalCommissionRate() != null;
  });

  readonly finalOfferSubmittedAt = computed<Date | null>(() => {
    return this.application().engagement?.finalOfferSubmittedAt ?? null;
  });

  readonly proposedCommissionRate = computed<number | null>(() => {
    return this.application().engagement?.commissionRate ?? null;
  });

  readonly commissionRateDelta = computed<number | null>(() => {
    const init = this.initialCommissionRate();
    const final = this.finalCommissionRate();
    if (init == null || final == null) return null;
    return Number((final - init).toFixed(2));
  });

  readonly targetCommissionRate = computed<number | null>(() => {
    return this.property()?.mandate?.targetCommissionRate ?? null;
  });

  readonly sellingPriceEuros = computed<number | null>(() => {
    const cents = this.property()?.financials?.sellingPriceCents;
    return cents ? cents / 100 : null;
  });

  readonly surfaceHabitable = computed<number | null>(() => {
    const s = this.property()?.keyInfo?.surface?.habitable;
    return s && s > 0 ? s : null;
  });

  readonly dvfData = computed<GouvDvfDataDto | null>(() => {
    return this.property()?.geospatial?.dvf ?? null;
  });

  readonly valuation = computed(() => {
    return this.application().engagement?.valuation;
  });

  readonly recommendedPriceEuros = computed<number | null>(() => {
    return this.valuation()?.recommendedPrice ?? null;
  });

  readonly attractivePriceEuros = computed<number | null>(() => {
    return this.valuation()?.attractivePrice ?? null;
  });

  readonly heartStrokePriceEuros = computed<number | null>(() => {
    return this.valuation()?.heartStrokePrice ?? null;
  });

  readonly referencePriceEuros = computed<number | null>(() => {
    return this.recommendedPriceEuros() ?? this.sellingPriceEuros();
  });

  readonly agentPricePerM2 = computed<number | null>(() => {
    const price = this.recommendedPriceEuros();
    const surface = this.surfaceHabitable();
    if (!price || !surface) return null;
    return Math.round(price / surface);
  });

  readonly ownerPricePerM2 = computed<number | null>(() => {
    const price = this.sellingPriceEuros();
    const surface = this.surfaceHabitable();
    if (!price || !surface) return null;
    return Math.round(price / surface);
  });

  readonly priceDifferencePercent = computed<number | null>(() => {
    const recommended = this.recommendedPriceEuros();
    const selling = this.sellingPriceEuros();
    if (!recommended || !selling) return null;
    const diff = ((recommended - selling) / selling) * 100;
    return Number(diff.toFixed(1));
  });

  readonly dvfMedianDiffPercent = computed<number | null>(() => {
    const agentM2 = this.agentPricePerM2();
    const dvfMedian = this.dvfData()?.medianPriceM2;
    if (!agentM2 || !dvfMedian) return null;
    return Math.round(((agentM2 - dvfMedian) / dvfMedian) * 100);
  });

  readonly estimatedCommissionEuros = computed<number | null>(() => {
    const price = this.referencePriceEuros();
    const rate = this.proposedCommissionRate();
    if (!price || !rate) return null;
    return Math.round((price * rate) / 100);
  });

  readonly totalFaiEuros = computed<number | null>(() => {
    const price = this.referencePriceEuros();
    const comm = this.estimatedCommissionEuros();
    if (!price) return null;
    return price + (comm ?? 0);
  });

  readonly hasValuationPrices = computed<boolean>(() => {
    const val = this.valuation();
    return Boolean(val && (val.recommendedPrice || val.attractivePrice || val.heartStrokePrice));
  });

  readonly hasValuationAnalysis = computed<boolean>(() => {
    const val = this.valuation();
    return Boolean(
      val &&
      (val.generalPresentation ||
        (val.strengths && val.strengths.length > 0) ||
        (val.weaknesses && val.weaknesses.length > 0)),
    );
  });

  readonly services = computed<PropertyApplicationService[]>(() => {
    return this.application().engagement?.services || [];
  });

  getServiceLabel(service: PropertyApplicationService): string {
    return EtxeEnumsNiceLabelUtils.applicationService(service);
  }

  getServiceIcon(service: PropertyApplicationService): string {
    return EtxeEnumsNiceLabelUtils.applicationServiceIcon(service);
  }

  getServiceDescription(service: PropertyApplicationService): string {
    return EtxeEnumsNiceLabelUtils.applicationServiceDescription(service);
  }
}
